import React, { useState } from 'react';

/**
 * CSAI-styled confirmation dialog, used instead of the browser's window.confirm. Matches the
 * look of the other collection modals (NamePromptModal): card on a dimmed overlay, title, message,
 * Cancel + confirm buttons. onConfirm may throw: the error is shown inline and the dialog stays open.
 */
interface ConfirmModalProps {
  title: string;
  message: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean; // red confirm button (delete-style actions)
  onConfirm: () => Promise<void> | void;
  onCancel: () => void;
}

const TEXT = 'var(--text-accent)';

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  title,
  message,
  confirmLabel,
  cancelLabel = 'Cancel',
  destructive = false,
  onConfirm,
  onCancel,
}) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const confirm = async () => {
    try {
      setBusy(true);
      setErr(null);
      await onConfirm();
    } catch (e) {
      console.error('Confirm action failed:', e);
      setErr('Something went wrong. Please try again.');
      setBusy(false);
    }
  };

  return (
    <div
      onClick={busy ? undefined : onCancel}
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
        onClick={(e) => e.stopPropagation()}
        style={{ backgroundColor: 'var(--bg-card)', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '440px' }}
      >
        <h3 id="confirm-modal-title" style={{ margin: '0 0 12px', fontSize: '1.125rem', fontWeight: 600, color: TEXT }}>
          {title}
        </h3>
        <div style={{ fontSize: '0.9375rem', lineHeight: 1.5, color: TEXT }}>{message}</div>
        {err && <div style={{ color: 'var(--error-text)', fontSize: '0.8125rem', marginTop: '12px' }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
          <button className="btn btn-ghost" onClick={onCancel} disabled={busy}>{cancelLabel}</button>
          <button
            className="btn btn-primary"
            onClick={confirm}
            disabled={busy}
            style={destructive
              ? { backgroundColor: 'var(--error-bg)', borderColor: 'var(--error-border)', color: 'var(--error-text)' }
              : undefined}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
